import {
  breakpointSizes,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import Box from 'src/components/Box/Box'
import EmptyLabel from 'src/components/EmptyLabel/EmptyLabel'
import { ExpenseFields } from 'src/components/ExpenseForm/ExpenseForm'
import ReceiptsBox from 'src/components/ReceiptsBox/ReceiptsBox'
import { ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import Trns from 'src/localization/Trns'
import { ExpenseType } from 'src/models/expenseReport.model'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { css } from 'styled-components'

interface ExpenseFlyoutContentProps {
  expenseReportId: string
  receiptAttachments: ReceiptAttachmentModel[]
  expenseFlatFields?: ExpenseFields
  notesAndReceiptsEditable: boolean
  isExpenseTypeMileage: boolean
  mileageRate: number
}

const ExpenseFlyoutContent: React.FC<ExpenseFlyoutContentProps> = (
  props,
): React.ReactElement => {
  const getDecimalPlaces = (num: number): number => {
    const index = num.toString().indexOf('.')
    if (index === -1) {
      return 0
    }
    return num.toString().length - index - 1
  }

  return (
    <>
      <Box
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              14,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 8px;
          `}
        >
          <Trns source={'Expense Type'} />
        </span>
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {props.expenseFlatFields?.type === ExpenseType.General ? (
            <Trns source={'General'} />
          ) : (
            <Trns source={'Mileage'} />
          )}
        </span>
      </Box>
      <Box
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <div>
          <span
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              margin-bottom: 8px;
              display: flex;
            `}
          >
            <Trns source={'Expense Name'} />
          </span>
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {props.expenseFlatFields?.expenseName || '--'}
          </span>
        </div>
        {props.isExpenseTypeMileage && (
          <div>
            <span
              css={css`
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
                margin-bottom: 8px;
                margin-top: 28px;
                display: flex;
              `}
            >
              <Trns source={'Distance'} />
            </span>
            {props.expenseFlatFields?.distance ? (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                <span
                  css={css`
                    margin-right: 5px;
                  `}
                >
                  {props.expenseFlatFields?.distance}
                </span>
                <span>
                  <Trns source={'miles'} />
                </span>
              </span>
            ) : (
              <EmptyLabel />
            )}
          </div>
        )}
        <div>
          <span
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              margin-bottom: 8px;
              margin-top: 28px;
              display: flex;
            `}
          >
            {props.isExpenseTypeMileage ? (
              <Trns source={'Amount to be reimbursed'} />
            ) : (
              <Trns source={'Amount Spent'} />
            )}
          </span>
          {props.expenseFlatFields?.amountCents ? (
            <span
              css={css`
                ${props.isExpenseTypeMileage
                  ? css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.regular,
                      )};
                    `
                  : css`
                      ${getFontCss(
                        16,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
              `}
            >
              <Trns
                source={'${threshold} ${faintText}'}
                expressions={{
                  threshold: (
                    <Crncy
                      currency={ExtendCurrency.Usd}
                      amount={props.expenseFlatFields?.amountCents}
                    />
                  ),
                  faintText: (
                    <span
                      css={css`
                        ${props.isExpenseTypeMileage
                          ? css`
                              ${getFontCss(
                                14,
                                styleConstants.colors.gray_darkest,
                                styleConstants.fontFamilies.regular,
                              )};
                            `
                          : css`
                              ${getFontCss(
                                16,
                                styleConstants.colors.gray_darkest,
                                styleConstants.fontFamilies.regular,
                              )};
                            `}
                      `}
                    >
                      {ExtendCurrency.Usd}
                    </span>
                  ),
                }}
              />
            </span>
          ) : (
            <EmptyLabel />
          )}
          {props.isExpenseTypeMileage && (
            <span
              css={css`
                ${getFontCss(
                  12,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
                margin-left: 10px;
              `}
            >
              <Trns
                source={'@ ${mileageRate}/mile'}
                expressions={{
                  mileageRate: (
                    <Crncy
                      currency={ExtendCurrency.Usd}
                      amount={props.mileageRate ? props.mileageRate : 0}
                      maximumFractionDigits={
                        getDecimalPlaces(props.mileageRate) > 2 ? 3 : 2
                      }
                    />
                  ),
                }}
              />
            </span>
          )}
        </div>
        <div>
          <span
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              margin-bottom: 8px;
              margin-top: 28px;
              display: flex;
            `}
          >
            {props.isExpenseTypeMileage ? (
              <Trns source={'Date Traveled'} />
            ) : (
              <Trns source={'Date of Purchase'} />
            )}
          </span>
          {props.expenseFlatFields?.expenseDate !== undefined ? (
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {props.expenseFlatFields?.expenseDate.format('LL')}
            </span>
          ) : (
            <EmptyLabel />
          )}
        </div>
      </Box>
      {!props.notesAndReceiptsEditable && (
        <>
          {!props.isExpenseTypeMileage && (
            <ReceiptsBox
              attachments={props.receiptAttachments}
              isReceiptRequired={false}
            />
          )}
          <Box
            noPadding
            css={css`
              display: flex;
              flex-direction: column;
              padding: 36px 40px;
              ${getBreakpointOnly(breakpointSizes.XXSM)(css`
                padding: 36px 20px;
              `)}
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                margin-bottom: 30px;
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                <Trns source={'Notes'} />
              </span>
            </div>
            {props.expenseFlatFields?.notes ? (
              <span
                css={css`
                  word-break: break-word;
                `}
              >
                {props.expenseFlatFields?.notes}
              </span>
            ) : (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.italic,
                  )};
                  display: flex;
                  justify-content: center;
                `}
              >
                <Trns source={'No notes have been entered'} />
              </span>
            )}
          </Box>
        </>
      )}
    </>
  )
}

export default ExpenseFlyoutContent
